import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ImageIcon from '@mui/icons-material/Image';
import RichTextEditor from '../Layouts/RichTextEditor';
import iconMap from '../../utils/iconMap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const contentTypeLabels = { link: "URL", text: "Text (no link)", phone: "Phone Number", email: "Email Address" };

const IconSelect = ({ value, onChange, label = "Icon" }) => (
    <TextField label={label} select variant="outlined" size="small" value={value || ""} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: 160 }}>
        {Object.entries(iconMap).map(([key, { label: iconLabel }]) => (
            <MenuItem value={key} key={key || "none"}>{iconLabel}</MenuItem>
        ))}
    </TextField>
);

const HeaderAdmin = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const { user } = useSelector((state) => state.user);
    const canEdit = user.role === "admin" || user.adminRole?.permissions?.header?.edit;

    const [topBarEnabled, setTopBarEnabled] = useState(true);
    const [topBarBgColor, setTopBarBgColor] = useState("#F7941D");
    const [topBarContent, setTopBarContent] = useState("");
    const [topBarContentZh, setTopBarContentZh] = useState("");

    const [utilityLeftItems, setUtilityLeftItems] = useState([]);
    const [navItems, setNavItems] = useState([]);

    const [logo, setLogo] = useState("");
    const [logoPreview, setLogoPreview] = useState("");
    const [logoWidth, setLogoWidth] = useState(48);

    const [favicon, setFavicon] = useState("");
    const [faviconPreview, setFaviconPreview] = useState("");

    const [showWishlist, setShowWishlist] = useState(true);
    const [showCart, setShowCart] = useState(true);
    const [showUserProfile, setShowUserProfile] = useState(true);
    const [showMoreMenu, setShowMoreMenu] = useState(true);
    const [showWallet, setShowWallet] = useState(true);

    const [androidAppUrl, setAndroidAppUrl] = useState("");
    const [iosAppUrl, setIosAppUrl] = useState("");
    const [appDownloadStatusText, setAppDownloadStatusText] = useState("Coming soon");
    const [appDownloadStatusTextZh, setAppDownloadStatusTextZh] = useState("");
    const [driverAndroidAppUrl, setDriverAndroidAppUrl] = useState("");
    const [driverIosAppUrl, setDriverIosAppUrl] = useState("");

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const { data } = await axios.get('/api/v1/header-settings');
                const s = data.settings;
                setTopBarEnabled(s.topBarEnabled);
                setTopBarBgColor(s.topBarBgColor || "#F7941D");
                setTopBarContent(s.topBarContent || "");
                setTopBarContentZh(s.topBarContentZh || "");
                setUtilityLeftItems(s.utilityLeftItems || []);
                setNavItems(s.navItems || []);
                setLogoPreview(s.logo?.url || "");
                setLogoWidth(s.logoWidth || 48);
                setFaviconPreview(s.favicon?.url || "");
                setShowWishlist(s.showWishlist);
                setShowCart(s.showCart);
                setShowUserProfile(s.showUserProfile);
                setShowMoreMenu(s.showMoreMenu);
                setShowWallet(s.showWallet);
                setAndroidAppUrl(s.androidAppUrl || "");
                setIosAppUrl(s.iosAppUrl || "");
                setAppDownloadStatusText(s.appDownloadStatusText || "Coming soon");
                setAppDownloadStatusTextZh(s.appDownloadStatusTextZh || "");
                setDriverAndroidAppUrl(s.driverAndroidAppUrl || "");
                setDriverIosAppUrl(s.driverIosAppUrl || "");
            } catch (error) {
                enqueueSnackbar("Failed to load header settings", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [enqueueSnackbar]);

    // --- Utility left items ---
    const updateLeftItem = (index, patch) => {
        setUtilityLeftItems((prev) => prev.map((item, i) => i === index ? { ...item, ...patch } : item));
    };
    const addLeftItem = () => {
        setUtilityLeftItems((prev) => [...prev, { icon: "", contentType: "text", label: "", labelZh: "", value: "" }]);
    };
    const removeLeftItem = (index) => {
        setUtilityLeftItems((prev) => prev.filter((_, i) => i !== index));
    };

    // --- Nav items ---
    const updateNavItem = (index, patch) => {
        setNavItems((prev) => prev.map((item, i) => i === index ? { ...item, ...patch } : item));
    };
    const addNavItem = () => {
        setNavItems((prev) => [...prev, { label: "", labelZh: "", url: "", icon: "" }]);
    };
    const removeNavItem = (index) => {
        setNavItems((prev) => prev.filter((_, i) => i !== index));
    };
    const moveNavItem = (index, direction) => {
        setNavItems((prev) => {
            const next = [...prev];
            const target = index + direction;
            if (target < 0 || target >= next.length) return prev;
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                setLogoPreview(reader.result);
                setLogo(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleFaviconChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            if (reader.readyState === 2) {
                setFaviconPreview(reader.result);
                setFavicon(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                topBarEnabled, topBarBgColor, topBarContent, topBarContentZh,
                utilityLeftItems, navItems,
                logoWidth,
                showWishlist, showCart, showUserProfile, showMoreMenu, showWallet,
                androidAppUrl, iosAppUrl, appDownloadStatusText, appDownloadStatusTextZh,
                driverAndroidAppUrl, driverIosAppUrl,
            };
            if (logo) payload.logo = logo;
            if (favicon) payload.favicon = favicon;

            await axios.put('/api/v1/admin/header-settings', payload);
            enqueueSnackbar("Header settings updated", { variant: "success" });
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title="Admin: Header | Star Mart" />
            {saving && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <button onClick={() => navigate('/admin/site-content')} className="text-sm text-primary-blue hover:underline">&larr; Back to Site Content</button>
                <h1 className="text-lg font-medium uppercase">Header</h1>
            </div>

            <div className="flex flex-col gap-8 bg-white rounded-lg shadow p-4 sm:p-6">

                {/* Row 1: Top Bar */}
                <div className="flex flex-col gap-3">
                    <h2 className="font-medium text-lg">Row 1: Top Bar</h2>
                    <FormControlLabel
                        control={<Checkbox checked={topBarEnabled} onChange={(e) => setTopBarEnabled(e.target.checked)} />}
                        label="Enabled"
                    />
                    <div className="flex items-center gap-3">
                        <label className="text-sm text-gray-600">Background Color:</label>
                        <input type="color" value={topBarBgColor} onChange={(e) => setTopBarBgColor(e.target.value)} className="w-12 h-9 border rounded cursor-pointer" />
                        <span className="text-sm text-gray-500">{topBarBgColor}</span>
                    </div>
                    <div>
                        <p className="text-sm text-gray-600 mb-1">Content</p>
                        <RichTextEditor value={topBarContent} onChange={setTopBarContent} />
                    </div>
                    <div>
                        <p className="text-sm text-gray-600 mb-1">Content (中文 — optional)</p>
                        <RichTextEditor value={topBarContentZh} onChange={setTopBarContentZh} />
                    </div>
                </div>

                <hr />

                {/* Row 2 left: Utility Items */}
                <div className="flex flex-col gap-3">
                    <h2 className="font-medium text-lg">Row 2 (Left): Utility Items</h2>
                    {utilityLeftItems.map((item, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2 border rounded-lg p-3 bg-gray-50">
                            <IconSelect value={item.icon} onChange={(val) => updateLeftItem(i, { icon: val })} />
                            <TextField
                                label="Content Type"
                                select
                                variant="outlined"
                                size="small"
                                value={item.contentType}
                                onChange={(e) => updateLeftItem(i, { contentType: e.target.value })}
                                sx={{ minWidth: 160 }}
                            >
                                {Object.entries(contentTypeLabels).map(([key, label]) => (
                                    <MenuItem value={key} key={key}>{label}</MenuItem>
                                ))}
                            </TextField>
                            <TextField label="Label" variant="outlined" size="small" value={item.label} onChange={(e) => updateLeftItem(i, { label: e.target.value })} />
                            <TextField label="Label (中文)" variant="outlined" size="small" value={item.labelZh || ""} onChange={(e) => updateLeftItem(i, { labelZh: e.target.value })} />
                            {item.contentType !== "text" && (
                                <TextField
                                    label={item.contentType === "phone" ? "Phone Number" : item.contentType === "email" ? "Email Address" : "URL"}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={item.value}
                                    onChange={(e) => updateLeftItem(i, { value: e.target.value })}
                                />
                            )}
                            {canEdit && (
                                <button type="button" onClick={() => removeLeftItem(i)} className="text-red-500 hover:text-red-700 p-2"><DeleteIcon /></button>
                            )}
                        </div>
                    ))}
                    {canEdit && (
                        <button type="button" onClick={addLeftItem} className="flex items-center gap-1 text-sm text-primary-blue hover:underline w-fit">
                            <AddIcon sx={{ fontSize: "18px" }} /> Add Item
                        </button>
                    )}
                </div>

                <hr />

                {/* Row 2 right: Nav Items */}
                <div className="flex flex-col gap-3">
                    <h2 className="font-medium text-lg">Row 2 (Right): Navigation Links</h2>
                    {navItems.map((item, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2 border rounded-lg p-3 bg-gray-50">
                            <TextField label="Label" variant="outlined" size="small" value={item.label} onChange={(e) => updateNavItem(i, { label: e.target.value })} />
                            <TextField label="Label (中文)" variant="outlined" size="small" value={item.labelZh || ""} onChange={(e) => updateNavItem(i, { labelZh: e.target.value })} />
                            <TextField label="Link" variant="outlined" size="small" fullWidth value={item.url} onChange={(e) => updateNavItem(i, { url: e.target.value })} helperText="e.g. /products or https://..." />
                            <IconSelect value={item.icon} onChange={(val) => updateNavItem(i, { icon: val })} label="Icon (optional)" />
                            {canEdit && (
                                <div className="flex items-center gap-1">
                                    <button type="button" onClick={() => moveNavItem(i, -1)} className="text-gray-500 hover:text-gray-700 p-1"><ArrowUpwardIcon sx={{ fontSize: "18px" }} /></button>
                                    <button type="button" onClick={() => moveNavItem(i, 1)} className="text-gray-500 hover:text-gray-700 p-1"><ArrowDownwardIcon sx={{ fontSize: "18px" }} /></button>
                                    <button type="button" onClick={() => removeNavItem(i)} className="text-red-500 hover:text-red-700 p-1"><DeleteIcon sx={{ fontSize: "18px" }} /></button>
                                </div>
                            )}
                        </div>
                    ))}
                    {canEdit && (
                        <button type="button" onClick={addNavItem} className="flex items-center gap-1 text-sm text-primary-blue hover:underline w-fit">
                            <AddIcon sx={{ fontSize: "18px" }} /> Add Link
                        </button>
                    )}
                </div>

                <hr />

                {/* Row 3: Logo + icon toggles */}
                <div className="flex flex-col gap-3">
                    <h2 className="font-medium text-lg">Row 3: Logo &amp; Icons</h2>

                    <div className="flex items-center gap-4">
                        <div className="w-32 h-16 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                            {!logoPreview ? <ImageIcon className="text-gray-400" /> :
                                <img draggable="false" src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain" />
                            }
                        </div>
                        <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg text-sm">
                            <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                            Choose Logo
                        </label>
                        <TextField
                            label="Logo Width (px)"
                            type="number"
                            variant="outlined"
                            size="small"
                            value={logoWidth}
                            onChange={(e) => setLogoWidth(Number(e.target.value))}
                            sx={{ maxWidth: 160 }}
                        />
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                            {!faviconPreview ? <ImageIcon className="text-gray-400" /> :
                                <img draggable="false" src={faviconPreview} alt="Favicon" className="max-w-full max-h-full object-contain" />
                            }
                        </div>
                        <label className="rounded bg-gray-400 text-center cursor-pointer text-white py-2 px-3 shadow hover:shadow-lg text-sm">
                            <input type="file" accept="image/*" onChange={handleFaviconChange} className="hidden" />
                            Choose Favicon
                        </label>
                        <p className="text-xs text-gray-400 max-w-xs">Shown in the browser tab. A square image works best.</p>
                    </div>

                    <div className="flex flex-wrap gap-4 mt-2">
                        <FormControlLabel control={<Checkbox checked={showWishlist} onChange={(e) => setShowWishlist(e.target.checked)} />} label="Show Wishlist Icon" />
                        <FormControlLabel control={<Checkbox checked={showCart} onChange={(e) => setShowCart(e.target.checked)} />} label="Show Cart Icon" />
                        <FormControlLabel control={<Checkbox checked={showUserProfile} onChange={(e) => setShowUserProfile(e.target.checked)} />} label="Show User Profile Icon" />
                        <FormControlLabel control={<Checkbox checked={showMoreMenu} onChange={(e) => setShowMoreMenu(e.target.checked)} />} label="Show 'More' Menu" />
                        <FormControlLabel control={<Checkbox checked={showWallet} onChange={(e) => setShowWallet(e.target.checked)} />} label="Show 1313 Wallet in account menu" />
                    </div>
                </div>

                {/* App Download settings */}
                <div className="flex flex-col gap-3">
                    <h2 className="font-medium text-lg">App Download Links</h2>
                    <p className="text-sm text-gray-500">
                        Used by the "Download App" option in the header's More menu, and by the footer's
                        download section. Leave a link blank to show the status text instead (e.g. "Coming soon").
                    </p>
                    <TextField
                        label="Android (Google Play) URL"
                        variant="outlined"
                        size="small"
                        value={androidAppUrl}
                        onChange={(e) => setAndroidAppUrl(e.target.value)}
                        placeholder="https://play.google.com/store/apps/details?id=..."
                    />
                    <TextField
                        label="iOS (App Store) URL"
                        variant="outlined"
                        size="small"
                        value={iosAppUrl}
                        onChange={(e) => setIosAppUrl(e.target.value)}
                        placeholder="https://apps.apple.com/app/..."
                    />
                    <TextField
                        label="Status Text (shown when a link is blank)"
                        variant="outlined"
                        size="small"
                        value={appDownloadStatusText}
                        onChange={(e) => setAppDownloadStatusText(e.target.value)}
                    />
                    <TextField
                        label="Status Text (中文 — optional)"
                        variant="outlined"
                        size="small"
                        value={appDownloadStatusTextZh}
                        onChange={(e) => setAppDownloadStatusTextZh(e.target.value)}
                    />
                </div>

                {/* Driver App Download settings */}
                <div className="flex flex-col gap-3">
                    <h2 className="font-medium text-lg">Driver App Download Links</h2>
                    <p className="text-sm text-gray-500">
                        Used by the driver app's own download page at <code>/driver-app</code> — not shown anywhere
                        in the customer-facing site.
                    </p>
                    <TextField
                        label="Driver App Android (Google Play) URL"
                        variant="outlined"
                        size="small"
                        value={driverAndroidAppUrl}
                        onChange={(e) => setDriverAndroidAppUrl(e.target.value)}
                        placeholder="https://1313mart.com/downloads/1313mart-driver.apk"
                    />
                    <TextField
                        label="Driver App iOS (App Store) URL"
                        variant="outlined"
                        size="small"
                        value={driverIosAppUrl}
                        onChange={(e) => setDriverIosAppUrl(e.target.value)}
                        placeholder="https://apps.apple.com/app/..."
                    />
                </div>

                {canEdit && (
                    <div className="flex justify-end">
                        <button onClick={handleSave} className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg">
                            Save Changes
                        </button>
                    </div>
                )}

            </div>
        </>
    );
};

export default HeaderAdmin;
